import { useCallback, useEffect, useState } from 'react';

import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  MutationDefinition,
} from '@reduxjs/toolkit/dist/query';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { Button, Checkbox, Col, Flex, Form, type FormInstance, Modal, Row, Space, Typography } from 'antd';
import { type Rule } from 'antd/lib/form';
import { ModalDivider } from 'components/modal-divider';
import { useMessageLogger } from 'hooks/useMessageLogger';
import { layoutModel } from 'layout/main-layout';
import { useOutletContext } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'services/redux/configureStore';
import { updateCurrentCountryAction, updateCurrentOfferAction } from 'services/redux/offers/actions';
import { width } from 'utils/helpers/width';

import styles from './index.module.css';

interface ModalFormProps {
  width?: number | string;
  title: string;
  isModalVisible: boolean;
  setIsModalVisible: (state: boolean) => void;
  mutation: UseMutation<
    MutationDefinition<
      any,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
      any,
      any,
      'api'
    >
  >;
  fields: FieldData[];
  fieldsToSet?: Record<string, unknown> | undefined;
  noResetFields?: string[];
  successMessage?: string;
  redirectTo?: (id: React.Key) => void;
  resetAdditional?: VoidFunction;
  resendDefaults?: boolean;
  noCloseAndRest?: boolean;
  normalizeValues?: (values: any) => any;
  afterSuccess?: VoidFunction;
  afterClose?: VoidFunction;
  isOffer?: boolean;
  isGeo?: boolean;
  isTracker?: boolean;
  tracker?: number;
  isAddLanding?: boolean;
  isAddDomain?: boolean;
  selectedGroupType?: string;
  currentTrackerId?: number;
  externalForm?: FormInstance;
}

export interface FieldData {
  label: string;
  name: string | string[];
  colon?: boolean;
  required?: boolean;
  hidden?: boolean;
  noRender?: boolean;
  render?: JSX.Element;
  valuePropName?: string;
  noSend?: boolean;
  normalize?: (value: any) => any;
  rules?: Rule[];
}

const ModalForm = ({
  width: modalWidth = 800,
  isModalVisible,
  setIsModalVisible,
  successMessage,
  title,
  mutation,
  redirectTo,
  fields,
  fieldsToSet,
  resendDefaults,
  resetAdditional,
  normalizeValues,
  afterSuccess,
  afterClose,
  isOffer = false,
  isGeo = false,
  isTracker = false,
  isAddLanding = false,
  isAddDomain = false,
  tracker,
  selectedGroupType,
  currentTrackerId,
  externalForm,
  noResetFields,
}: ModalFormProps) => {
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const [mutaionReq, { isLoading, error, data, status }] = mutation();
  const [noClose, setNoClose] = useState(false);
  const [noReset, setNoReset] = useState(false);
  const dispatch = useAppDispatch();
  const categories = useAppSelector((store) => store.offers.categories);
  const affiliates = useAppSelector((store) => store.offers.affiliates);
  const user = useOutletContext<layoutModel.OutletContext>();
  const activeTrackers = user.trackers?.filter((el) => el.permissions.canUpload === true);

  const handleSubmit = async (values: any) => {
    if (activeTrackers && isAddLanding) {
      if (activeTrackers?.length === 1) {
        values.trackerId = activeTrackers[0].id;
        values.type = selectedGroupType;
      } else {
        values.trackerId = tracker;
        values.type = selectedGroupType;
      }
    }

    if (isGeo) {
      dispatch(updateCurrentCountryAction(values));
    }

    if (isAddDomain) {
      if (!values.method) values.method = 'ADD';
    }

    if (isOffer) {
      if (affiliates && categories) {
        dispatch(
          updateCurrentOfferAction({
            affiliate: affiliates.filter((el) => el.id === values.affiliateId)[0],
            category: categories.filter((el) => el.id === values.categoryId)[0],
            description: values.description,
            externalId: values.externalId,
            id: values.id,
            image: values.image,
            isActive: values.active,
            name: values.name,
            inArchive: false,
            subtitle: values.subtitle,
            createdAt: '',
            updatedAt: '',
            isDirect: values.isDirect ?? false,
          })
        );
      }
    }

    let normalizedValues = values;
    if (normalizeValues) {
      normalizedValues = normalizeValues(values);
    }

    await mutaionReq(normalizedValues);
  };

  const resetForm = useCallback(() => {
    if (noResetFields?.length && noClose) {
      const preservedValues = form.getFieldsValue(noResetFields);

      form.resetFields();
      form.setFieldsValue(preservedValues);
    } else {
      form.resetFields();
    }
    resetAdditional && resetAdditional();
  }, [form, resetAdditional, noResetFields]);

  useEffect(
    () => {
      if (fieldsToSet) form.setFieldsValue(fieldsToSet);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    resendDefaults ? undefined : []
  );

  useEffect(() => {
    if (currentTrackerId) {
      form.setFieldValue('groupId', undefined);
    }
  }, [currentTrackerId]);

  const onSuccess = () => {
    if (!noReset) {
      resetForm();
    }
    if (!noClose) {
      setIsModalVisible(false);
    }
    data?.data?.id && redirectTo && redirectTo(data.data.id);
    afterSuccess && afterSuccess();
  };

  useMessageLogger({ error, successMessage: successMessage || 'Запрос успешный', status, onSuccess });

  return (
    <>
      <Modal
        width={modalWidth}
        title={title}
        open={isModalVisible}
        destroyOnClose={true}
        confirmLoading={isLoading}
        onCancel={() => {
          setIsModalVisible(false);
          resetForm();
          afterClose && afterClose();
        }}
        footer={
          <Row justify="space-between" style={{ flexDirection: width < 820 ? 'column' : 'row' }}>
            <Flex vertical={width < 820} gap={8}>
              <Space style={{ userSelect: 'none' }}>
                <Checkbox checked={noClose} onChange={(value) => setNoClose(value.target.checked)}>
                  <Typography.Text type={noClose ? undefined : 'secondary'}>
                    Не закрывать форму?
                  </Typography.Text>
                </Checkbox>
                <Checkbox checked={noReset} onChange={(value) => setNoReset(value.target.checked)}>
                  <Typography.Text type={noReset ? undefined : 'secondary'}>
                    Не сбрасывать форму?
                  </Typography.Text>
                </Checkbox>
              </Space>
            </Flex>
            <Space className={styles.modal__buttons} style={{ marginLeft: width < 820 ? 'auto' : '' }}>
              <Button
                key="back"
                onClick={() => {
                  setIsModalVisible(false);
                  resetForm();
                  afterClose && afterClose();
                }}
              >
                Отмена
              </Button>
              <Button
                key="submit"
                type="primary"
                loading={isLoading}
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      form.submit();
                    })
                    .catch(() => {});
                }}
              >
                Подтвердить
              </Button>
            </Space>
          </Row>
        }
      >
        <ModalDivider type="top" />
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {fields.map(
            ({
              name,
              label,
              colon,
              render,
              required = true,
              hidden,
              noRender,
              noSend,
              valuePropName,
              normalize,
              rules,
            }) => {
              if (noRender || !render) return null;
              if (noSend)
                return (
                  <Row
                    key={typeof name === 'string' ? name : name.join('-')}
                    style={{ marginBottom: '20px' }}
                    gutter={10}
                    className={styles.modal__row}
                  >
                    <Col span={8} style={{ textAlign: 'end' }}>
                      <Space>
                        {required && <span style={{ color: '#ff4d4f' }}>*</span>}
                        <span style={{ verticalAlign: 'middle' }}>{label + ':'}</span>
                      </Space>
                    </Col>
                    <Col span={16}>{render}</Col>
                  </Row>
                );

              return (
                <Form.Item
                  key={typeof name === 'string' ? name : name.join('-')}
                  label={label + (width < 575 ? ':' : '')}
                  colon={colon}
                  name={name}
                  className={styles.modal__sendRow}
                  rules={
                    rules?.length
                      ? rules?.concat([{ required: required, message: 'Заполните поле' }])
                      : [{ required: required, message: 'Заполните поле' }]
                  }
                  noStyle={hidden}
                  valuePropName={valuePropName}
                  normalize={normalize}
                >
                  {render}
                </Form.Item>
              );
            }
          )}
        </Form>
        <ModalDivider type="bottom" />
      </Modal>
    </>
  );
};

export default ModalForm;
